import { useMemo } from "react";

import { TableColumnType } from "@/components/@types";
import { FieldTable } from "@/components/organisms/Field";
import { Typography } from "@/components/ui/typography";

export type EntityBalanceMatrixEntity = {
  identifier: string;
  label: string;
};

export type EntityBalanceMatrixCell = {
  debtorIdentifier: string;
  creditorIdentifier: string;
  amount: number;
};

type EntityBalanceMatrixRow = {
  id: string;
  label: string;
} & Record<string, string>;

/** Who owes whom, read-only: a row per debtor, a column per creditor. */
export function ReportsViewEntityBalanceMatrix({
  entities,
  cells,
}: {
  entities: EntityBalanceMatrixEntity[];
  cells: EntityBalanceMatrixCell[];
}) {
  const valueOptions = useMemo(
    () => ({
      columns: [
        {
          label: "Owes →",
          type: "text",
          objectKey: "label",
          isDisabled: true,
          isSearchable: false,
          isLockedLeft: true,
          width: 160,
          getValue: (row: EntityBalanceMatrixRow) => row.label,
        },
        ...entities.map((creditor) => ({
          label: creditor.label,
          type: "currency",
          headerFieldType: "number",
          objectKey: creditor.identifier,
          isDisabled: true,
          isSearchable: false,
          width: 140,
          minWidth: 120,
          style: { textAlign: "right" },
          // Nothing owed, or a pair the report is not of, is left blank.
          getValue: (row: EntityBalanceMatrixRow) =>
            row[creditor.identifier] ?? "",
        })),
      ] as TableColumnType[],
    }),
    [entities],
  );

  const rows = useMemo(() => {
    const amountByPair = new Map(
      cells
        .filter((cell) => cell.amount > 0)
        .map((cell) => [
          `${cell.debtorIdentifier}:${cell.creditorIdentifier}`,
          String(cell.amount),
        ]),
    );
    return entities.map(
      (debtor) =>
        ({
          id: debtor.identifier,
          label: debtor.label,

          ...Object.fromEntries(
            entities.flatMap((creditor) => {
              const amount = amountByPair.get(
                `${debtor.identifier}:${creditor.identifier}`,
              );
              return amount ? [[creditor.identifier, amount]] : [];
            }),
          ),
        }) as EntityBalanceMatrixRow,
    );
  }, [entities, cells]);

  if (entities.length === 0) {
    return (
      <Typography level="body-sm" className="text-muted-foreground/80">
        No entities in scope.
      </Typography>
    );
  }

  return (
    <div className="flex-1 min-h-0 w-full flex flex-col overflow-hidden pl-3 pr-3 pt-2 pb-2">
      <div className="flex flex-col min-h-0 max-h-full [border-width:1px] [border-style:solid] border-border rounded-md shadow-xs overflow-hidden">
        <FieldTable
          value={rows as never}
          valueOptions={valueOptions}
          cellSize="md"
          overlayScrollbars
          style={{ width: "100%", height: "100%", flex: 1, minHeight: 0 }}
        />
      </div>
    </div>
  );
}
